import { Button, Card, Form, Input, Select, Space } from "antd";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import LockIcon from "../../../assets/icons/LockIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import CardTitle from "../../../components/common/CardTitle";
import Loader from "../../../components/ui/Loader";
import { mailRegex } from "../../../constants/constants";
import {
  useCreateUserMutation,
  useGetSingleUserQuery,
} from "../../../features/portal/user/userApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import ConfirmModalLayout from "../../../layouts/ConfirmModalLayout";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import { isValidTLD, selectSearchOption } from "../../../utils/utils";

export default function BusinessUserCreate() {
  const { id, viewId } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const backLink = `/admin/businesses/business-list/${id}`;
  const { open, handleOpen, handleClose } = useModal();
  const {
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
    data: detailsData,
  } = useGetSingleUserQuery(viewId || "", {
    skip: !viewId,
  });
  const [createUser, { isError, error, isLoading, isSuccess }] =
    useCreateUserMutation();
  useError(isError, error, "User create fail");
  useError(isDetailsError, detailsError, "User details get fail");

  const getRoll = Form.useWatch("role", form);

  const onFinish = (values: any) => {
    const { confirmPassword, ...rest } = values;
    const newData = {
      ...rest,
      company_id: Number(id),
    };

    createUser(newData);
  };

  useEffect(() => {
    if (Object.keys(detailsData?.data || {}).length && viewId) {
      form.setFieldsValue({
        username: detailsData?.data?.username,
        role: detailsData?.data?.role,
        email: detailsData?.data?.email,
        permission: detailsData?.data?.permission,
      });
    }
  }, [detailsData?.data, form, viewId]);

  useEffect(() => {
    if (isSuccess) {
      toast("success", "User created successfully");
      navigate(backLink);
    }
  }, [backLink, isSuccess, navigate]);

  return (
    <>
      <SidebarLayout title="Business Details " backLink={backLink}>
        {open?.type === "CONFIRM" && (
          <ConfirmModalLayout
            title={<span>Confirm Password Reset!</span>}
            subTitle={
              <span>
                {" "}
                Are you sure you want to reset the password for{" "}
                <span className="text-primary">Sheba Business?</span> This
                action will generate a new password and notify the user.
              </span>
            }
            buttonName="Yes, Reset"
            action={false}
            buttonBgDeep={true}
            handleButton={handleClose}
            open={open?.open}
            handleClose={handleClose}
          />
        )}

        {isDetailsLoading ? (
          <Loader />
        ) : (
          <Card className="w-full sm:w-4/5 md:w-3/5 ">
            <CardTitle>Business User {viewId ? "Details" : "Create"}</CardTitle>
            <Form
              className="custom-form"
              layout="vertical"
              requiredMark="optional"
              size="large"
              onFinish={onFinish}
              form={form}
            >
              <div className="grid grid-cols-2 gap-4 mt-2.5">
                <Form.Item
                  label="Full Name"
                  name="username"
                  rules={[
                    {
                      required: true,
                      message: "Please enter full name",
                    },
                    // {
                    //   validator: async (_: any, value: string) => {
                    //     if (!value) {
                    //       return Promise.resolve();
                    //     }

                    //     if (!mailRegex.test(value)) {
                    //       return Promise.reject(
                    //         new Error("Please enter a valid email")
                    //       );
                    //     }

                    //     if (!isValidTLD(value)) {
                    //       return Promise.reject(
                    //         new Error("Please enter a valid TLD")
                    //       );
                    //     }

                    //     return Promise.resolve();
                    //   },
                    // },
                  ]}
                >
                  <Input placeholder="Full Name" disabled={!!viewId} />
                </Form.Item>

                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: "Please enter email",
                    },
                    {
                      validator: async (_: any, value: string) => {
                        if (!value) {
                          return Promise.resolve();
                        }

                        if (!mailRegex.test(value)) {
                          return Promise.reject(
                            new Error("Please enter a valid email")
                          );
                        }

                        if (!isValidTLD(value)) {
                          return Promise.reject(
                            new Error("Please enter a valid TLD")
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="name@gmail.com" disabled={!!viewId} />
                </Form.Item>

                <Form.Item
                  label="User Type"
                  name="role"
                  rules={[
                    {
                      required: true,
                      message: "Please select role",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select"
                    size="large"
                    allowClear
                    showSearch
                    filterOption={selectSearchOption}
                    options={roleList}
                    suffixIcon={<SelectDownIcon />}
                    disabled={!!viewId}
                    onChange={() => {
                      form.setFieldsValue({ permission: undefined });
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label="Permission"
                  name="permission"
                  rules={[
                    {
                      required: true,
                      message: "Please select permission",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select"
                    size="large"
                    allowClear
                    showSearch
                    filterOption={selectSearchOption}
                    options={
                      getRoll === "admin"
                        ? adminPermissionList
                        : subAdminPermissionList
                    }
                    suffixIcon={<SelectDownIcon />}
                    disabled={!!viewId}
                  />
                </Form.Item>

                {viewId ? null : (
                  <>
                    <Form.Item
                      name="password"
                      label="New Password"
                      className="mb-[18px]"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!value) {
                              return Promise.reject(
                                new Error("Please enter your password")
                              );
                            }
                            if (value.length < 6 || value.length > 20) {
                              return Promise.reject(
                                new Error(
                                  "Password length must be between 6 and 20 characters"
                                )
                              );
                            }
                            // if (!/[A-Za-z]/.test(value)) {
                            //   return Promise.reject(
                            //     new Error(
                            //       "Password must contain at least one alphabet"
                            //     )
                            //   );
                            // }
                            // if (!/[0-9]/.test(value)) {
                            //   return Promise.reject(
                            //     new Error(
                            //       "Password must contain at least one number"
                            //     )
                            //   );
                            // }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input.Password
                        prefix={<LockIcon />}
                        placeholder="Password"
                      />
                    </Form.Item>

                    <Form.Item
                      name="confirmPassword"
                      label="Confirm Password"
                      className="mb-[18px]"
                      rules={[
                        {
                          required: true,
                          message: "Please enter confirm password",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue("password") === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                "Password and confirm password do not match"
                              )
                            );
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        prefix={<LockIcon />}
                        placeholder="Confirm Password"
                      />
                    </Form.Item>
                  </>
                )}
              </div>

              {/* <div className="flex items-center gap-2">
              <p className="text-[#2d3643] text-base font-medium leading-7 mb-2.5">
                Subscription Type:
              </p>
              <Form.Item
                name="target_type"
                initialValue="active"
                className="ml-3"
              >
                <Radio.Group className="custom-radio !h-auto" size="large">
                  {targetTypes?.map((el) => (
                    <Radio
                      className="text-header text-sm font-medium mr-3.5"
                      value={el?.value}
                      key={el?.value}
                    >
                      {el.label}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </div> */}

              {/* {userId && (
              <Item title="Password Reset" className="!border-b-0 !py-5 !pt-0">
                <Button
                  danger
                  className="flex gap-1 items-center"
                  onClick={() => {
                    handleOpen({ type: "CONFIRM" });
                  }}
                  icon={<KeyIcon />}
                >
                  Password Reset
                </Button>
              </Item>
            )} */}

              {!viewId && (
                <div className="border-t border-light bg-white">
                  <Space className="w-full mt-4" size={12}>
                    <Link to={backLink}>
                      <Button
                        className="normal-dark-button modal-button"
                        size="middle"
                      >
                        Cancel
                      </Button>
                    </Link>
                    <Button
                      className="modal-button"
                      type="primary"
                      htmlType="submit"
                      size="middle"
                      loading={isLoading}
                    >
                      {"Create"}
                    </Button>
                  </Space>
                </div>
              )}
            </Form>
          </Card>
        )}
      </SidebarLayout>
    </>
  );
}

export const targetTypes = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const roleList = [
  { label: "Admin", value: "admin" },
  { label: "Sub Admin", value: "sub-admin" },
];

const adminPermissionList = [{ label: "All", value: "all" }];

const subAdminPermissionList = [
  { label: "Checker", value: "checker" },
  { label: "Maker", value: "maker" },
  { label: "Approver", value: "approver" },
];
